import {
    collection,
    doc,
    getDoc,
    getDocs,
    addDoc,
    updateDoc,
    query,
    where,
    limit,
    getCountFromServer,
    collectionGroup,
    startAfter,
    Timestamp,
    Firestore,
    orderBy,
    or,
    documentId,
} from 'firebase/firestore';
import { User } from 'firebase/auth';
import { storage } from '../../../firebaseConfig';

// excel pacakge
import * as xlsx from 'xlsx';
import { ref, getDownloadURL } from 'firebase/storage';

// Types
interface UserData {
    id: string;
    companyId?: string;
    // Add other user fields here
    [key: string]: any; // For other user fields
}

// Dashboard
interface AddTaskRequest {
    leadId: string;
    dueDate: Timestamp;
    task: string;
}

interface FetchTaskRequest {
    leadId: string;
}

interface FetchTaskResponse {
    data: {
        tasks: TaskData[];
    }
}

interface TaskData {
    id: string;
    task: string;
    dueDate: Timestamp;
    isDone: boolean;
    createdAt: Timestamp;
}

// function to add task
export async function addTask(
    db: Firestore,
    user: User | null,
    request: AddTaskRequest
): Promise<FetchTaskResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can add task');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Add task to the task collection in the lead document which is a subcollection in the companies collection
        const taskCollection = collection(db, 'companies', userData.companyId, 'leads', request.leadId, 'tasks');
        await addDoc(taskCollection, {
            task: request.task,
            dueDate: request.dueDate,
            userId: user.uid,
            isDone: false,
            createdAt: Timestamp.now()
        });

        return (
            {
                data: {
                    tasks: []
                }
            }
        )

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding task';
        console.error('Error in addTask:', error);
        throw new Error(errorMessage);
    }
}

// function to get tasks
interface FetchTasksRequest {
    leadId: string;
}

interface FetchTasksResponse {
    data: {
        tasks: TaskData[];
    }
}

export async function fetchTasks(
    db: Firestore,
    user: User | null,
    request: FetchTasksRequest
): Promise<FetchTasksResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch tasks');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // check if lead exists
        // const leadDoc = await getDoc(doc(db, 'leads', request.leadId));
        // if (!leadDoc.exists()) {
        //     throw new Error('Lead does not exist');
        // }

        // Query tasks with the same leadId
        const tasksQuery = query(
            collection(db, 'companies', userData.companyId, 'leads', request.leadId, 'tasks'),
        );

        const tasksSnapshot = await getDocs(tasksQuery);

        const tasksList = tasksSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as TaskData[];

        return {
            data: {
                tasks: tasksList
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching tasks';
        console.error('Error in fetchTasks:', error);
        throw new Error(errorMessage);
    }
}

// function to update task
interface UpdateTaskRequest {
    leadId: string;
    taskId: string;
    isDone: boolean;
}

interface UpdateTaskResponse {
}

export async function updateTask(
    db: Firestore,
    user: User | null,
    request: UpdateTaskRequest
): Promise<UpdateTaskResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can update task');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // check if lead exists
        // const leadDoc = await getDoc(doc(db, 'leads', request.leadId));
        // if (!leadDoc.exists()) {
        //     throw new Error('Lead does not exist');
        // }

        // check if task exists
        const taskDoc = await getDoc(doc(db, 'companies', userData.companyId, 'leads', request.leadId, 'tasks', request.taskId));
        if (!taskDoc.exists()) {
            throw new Error('Task does not exist');
        }

        // Update task
        const taskRef = doc(db, 'companies', userData.companyId, 'leads', request.leadId, 'tasks', request.taskId);
        await updateDoc(taskRef, {
            isDone: request.isDone
        });

        return (
            {
                data: {
                    message: 'Task updated successfully'
                }
            }
        )

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating task';
        console.error('Error in updateTask:', error);
        throw new Error(errorMessage);
    }
}

// Updated interfaces
interface FetchCallLogsRequest {
    leadId: string | undefined;
    lastDocumentId?: string;
    lastCallLogTime?: Timestamp;
    pageSize?: number;
}

interface FetchCallLogsResponse {
    data: {
        callLogs: CallLogWithOutcomeData[];
        lastDocument: {
            id: string;
            lastCallTimestamp: Timestamp;
        }
        hasMore: boolean;
        // count: number;
    }
}

interface CallLogData {
    id: string;
    type: string;
    callTimestamp: Timestamp;
    duration: number;
    notes: string;
    recording: string;
    outcomeUid: string;
    userId: string;
    leadUid: string;
}

export interface CallLogWithOutcomeData extends CallLogData {
    outcomeName: string;
    userName: string;
    recordingUrl: string | null;
    callRecordingUrl?: string | null;
}

interface OutcomeData {
    name: string;
}

export async function fetchCallLogs(
    db: Firestore,
    user: UserData | null,
    request: FetchCallLogsRequest
): Promise<FetchCallLogsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch call logs');
        }
        // Get user document
        const userData = user;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Set default page size or use requested size
        const pageSize = request.pageSize || 6;

        // Query call logs with the same leadId
        let callLogsQuery = query(
            collection(db, 'companies', userData.companyId, 'callLogs'),
            where('leadUid', '==', request.leadId),
            orderBy('callTimestamp', 'desc'),
            limit(pageSize)
        );

        console.log('request.lastDocumentId:', request.lastDocumentId);
        console.log('request.lastCallLogTime:', request.lastCallLogTime);


        // Apply pagination if lastDocumentId exists
        if (request.lastDocumentId && request.lastCallLogTime) {
            try {
                const lastCallLogDoc = await getDoc(doc(db, 'companies', userData.companyId, 'callLogs', request.lastDocumentId));
                if (lastCallLogDoc.exists()) {
                    callLogsQuery = query(
                        collection(db, 'companies', userData.companyId, 'callLogs'),
                        where('leadUid', '==', request.leadId),
                        orderBy('callTimestamp', 'desc'),
                        startAfter(lastCallLogDoc),
                        limit(pageSize)
                    );
                }
            }
            catch (error) {
                console.error('Error fetching last call log:', error);
            }
        }

        const callLogsSnapshot = await getDocs(callLogsQuery);

        // Get all outcomes
        const outcomeQuery = query(
            collection(db, 'outcomes')
        );

        const outcomeSnapshot = await getDocs(outcomeQuery);

        // Create a map of outcome IDs to outcome names for efficient lookup
        const outcomeMap = new Map<string, string>();
        outcomeSnapshot.docs.forEach(doc => {
            const outcomeData = doc.data() as OutcomeData;
            outcomeMap.set(doc.id, outcomeData.name);
        });

        // Get all users 
        const usersQuery = query(
            collection(db, 'users'),
            where('companyId', '==', userData.companyId)
        );

        const usersSnapshot = await getDocs(usersQuery);

        // Create a map of user IDs to user names for efficient lookup
        const userMap = new Map<string, string>();
        usersSnapshot.docs.forEach(doc => {
            const userData = doc.data() as UserData;
            userMap.set(doc.id, userData.name);
        });

        // Map call logs and include outcome names and get recording URLs
        const callLogPromises = callLogsSnapshot.docs.map(async (doc) => {
            const callLogData = {
                id: doc.id,
                ...doc.data()
            } as CallLogData;

            // Get recording URL from storage if it exists
            let recordingUrl = null;
            if (callLogData.recording) {
                try {
                    const recordingPath = `companies/call_recordings/${userData.companyId}/${request.leadId}/${callLogData.recording}`;
                    const storageRef = ref(storage, recordingPath);
                    // Get download URL with 1 hour expiration (3600 seconds)
                    recordingUrl = await getDownloadURL(storageRef);
                } catch (error) {
                    console.error(`Error fetching recording for call log ${callLogData.id}:`, error);
                    // Don't throw here, just leave recordingUrl as null
                }
            }

            // Get callRecordingUrl directly from the document data if it exists
            const callRecordingUrl = doc.data().callRecordingUrl || null;
            // console.log('Call log data for ID:', callLogData.id, 'has callRecordingUrl:', callRecordingUrl);

            return {
                id: callLogData.id,
                type: callLogData.type,
                callTimestamp: callLogData.callTimestamp,
                duration: callLogData.duration,
                notes: callLogData.notes,
                outcomeUid: callLogData.outcomeUid,
                outcomeName: outcomeMap.get(callLogData.outcomeUid) || 'Unknown Outcome',
                userId: callLogData.userId,
                userName: userMap.get(callLogData.userId) || 'Unknown User',
                recording: callLogData.recording,
                recordingUrl: recordingUrl,
                callRecordingUrl: callRecordingUrl
            };
        });

        const callLogsList = await Promise.all(callLogPromises) as (CallLogWithOutcomeData & { recordingUrl: string | null })[];

        // check if there are more call logs
        const hasMore = callLogsList.length === pageSize;

        const lastDocument = callLogsSnapshot.docs[callLogsSnapshot.docs.length - 1] || null;

        // const totalCallLogSnapshot = await getCountFromServer(callLogsQuery);
        // const totalCallLogs = totalCallLogSnapshot.data().count;


        return {
            data: {
                callLogs: callLogsList,
                lastDocument: {
                    id: lastDocument?.id,
                    lastCallTimestamp: lastDocument?.data()?.callTimestamp
                },
                hasMore: hasMore,
                // count: totalCallLogs
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching call logs';
        console.error('Error in fetchCallLogs:', error);
        throw new Error(errorMessage);
    }
}

// function for fetching follw up of a lead
interface FetchFollowUpRequest {
    leadId: string | undefined;
    lastDocumentId?: string;
    lastFollowUpDate?: Timestamp;
    pageSize?: number;
}

interface FetchFollowUpResponse {
    data: {
        followUps: FollowUpWithUserData[];
        lastDocument: {
            id: string;
            lastFollowUpDate: Timestamp;
        }
        hasMore: boolean;
        // count: number;
    }
}

interface FollowUpData {
    id: string;
    followUp: string;
    followUpTime: Timestamp;
    userId: string;
    isDone: boolean;
    isEdited?: boolean;
    updatedAt?: Timestamp; // Add this field
    createdAt: Timestamp; // Add this field
}

export interface FollowUpWithUserData {
    id: string;
    text: string;
    time: Timestamp;
    type: string;
    userId: string;
    isDone: boolean;
    isEdited?: boolean;
    updatedAt?: Timestamp; // Add this field
    userName: string;
}

export async function fetchFollowUps(
    db: Firestore,
    user: UserData | null,
    request: FetchFollowUpRequest
): Promise<FetchFollowUpResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch follow ups');
        }

        // Get user document
        const userData = user;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Set default page size or use requested size
        const pageSize = request.pageSize || 6;

        // Query follow ups with the same leadId
        let followUpsQuery = query(
            collection(db, 'companies', userData.companyId, 'followUps'),
            where('leadId', '==', request.leadId),
            orderBy('followUpTime', 'desc'),
            limit(pageSize)
        );

        // Apply pagination if lastDocumentId exists
        if (request.lastDocumentId && request.lastFollowUpDate) {
            try {
                const lastFollowUpDoc = await getDoc(doc(db, 'companies', userData.companyId, 'followUps', request.lastDocumentId));
                if (lastFollowUpDoc.exists()) {
                    followUpsQuery = query(
                        collection(db, 'companies', userData.companyId, 'followUps'),
                        where('leadId', '==', request.leadId),
                        orderBy('followUpTime', 'desc'),
                        startAfter(lastFollowUpDoc),
                        limit(pageSize)
                    );
                }
            }
            catch (error) {
                console.error('Error fetching last follow up:', error);
            }
        }

        const followUpsSnapshot = await getDocs(followUpsQuery);

        // Get all users
        const usersQuery = query(
            collection(db, 'users'),
            where('companyId', '==', userData.companyId)
        );

        const usersSnapshot = await getDocs(usersQuery);
        // Create a map of user IDs to user names for efficient lookup
        const userMap = new Map<string, string>();
        usersSnapshot.docs.forEach(doc => {
            const userData = doc.data() as UserData;
            userMap.set(doc.id, userData.name);
        });

        // Map follow ups and include user names
        const followUpsList = followUpsSnapshot.docs.map(doc => {
            const followUpData = {
                id: doc?.id,
                ...doc?.data()
            } as FollowUpData;

            return {
                id: followUpData?.id,
                text: followUpData.followUp,
                time: followUpData.followUpTime,
                type: 'followUp', // Assuming type is always 'followUp'
                userId: followUpData.userId,
                isDone: followUpData.isDone,
                isEdited: followUpData.isEdited || false,
                createdAt: followUpData.createdAt, // Include createdAt field
                updatedAt: followUpData.updatedAt, // Include updatedAt field
                userName: userMap.get(followUpData.userId) || 'Unknown User'
            };
        }) as FollowUpWithUserData[];

        // check if there are more follow ups
        const hasMore = followUpsList.length === pageSize;

        const lastDocument = followUpsSnapshot.docs[followUpsSnapshot.docs.length - 1] || null;

        return {
            data: {
                followUps: followUpsList,
                lastDocument: {
                    id: lastDocument?.id,
                    lastFollowUpDate: lastDocument?.data()?.followUpTime
                },
                hasMore: hasMore,
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching follow ups';
        console.error('Error in fetchFollowUps:', error);
        throw new Error(errorMessage);
    }
}

// funtion for fetching remarks of a lead
interface FetchRemarksRequest {
    leadId: string | undefined;
    lastDocumentId?: string;
    lastRemarkDate?: Timestamp;
    pageSize?: number;
}

interface FetchRemarksResponse {
    data: {
        remarks: RemarkWithUserData[];
        lastDocument: {
            id: string;
            lastRemarkDate: Timestamp;
        }
        hasMore: boolean;
        // count: number;
    }
}

interface RemarkData {
    id: string;
    remark: string;
    createdAt: Timestamp;
}

export interface RemarkWithUserData {
    id: string;
    text: string;
    time: Timestamp;
    type: string;
    userId: string;
    isDone: boolean;
    userName: string;
}

export async function fetchRemarks(
    db: Firestore,
    user: UserData | null,
    request: FetchRemarksRequest
): Promise<FetchRemarksResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch remarks');
        }

        // Get user document
        const userData = user;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Set default page size or use requested size
        const pageSize = request.pageSize || 6;

        // Query remarks with the same leadId
        if (!request.leadId) {
            throw new Error('leadId is required to fetch remarks');
        }
        let remarksQuery = query(
            collection(db, 'companies', userData.companyId, 'leads', request.leadId, 'remarks'),
            orderBy('createdAt', 'desc'),
            limit(pageSize)
        );

        // Apply pagination if lastDocumentId exists
        if (
            request.lastDocumentId !== undefined &&
            request.lastRemarkDate !== undefined &&
            request.leadId !== undefined
        ) {
            try {
                const lastRemarkDoc = await getDoc(
                    doc(
                        db,
                        'companies',
                        userData.companyId,
                        'leads',
                        request.leadId,
                        'remarks',
                        request.lastDocumentId
                    )
                );
                if (lastRemarkDoc.exists()) {
                    remarksQuery = query(
                        collection(db, 'companies', userData.companyId, 'leads', request.leadId, 'remarks'),
                        orderBy('createdAt', 'desc'),
                        startAfter(lastRemarkDoc),
                        limit(pageSize)
                    );
                }
            }
            catch (error) {
                console.error('Error fetching last remark:', error);
            }
        }

        const remarksSnapshot = await getDocs(remarksQuery);

        // Get all users
        const usersQuery = query(
            collection(db, 'users'),
            where('companyId', '==', userData.companyId)
        );

        const usersSnapshot = await getDocs(usersQuery);
        // Create a map of user IDs to user names for efficient lookup
        const userMap = new Map<string, string>();
        usersSnapshot.docs.forEach(doc => {
            const userData = doc.data() as UserData;
            userMap.set(doc.id, userData.name);
        });

        // Map remarks and include user names
        const remarksList = remarksSnapshot.docs.map(doc => {
            const remarkData = {
                id: doc.id,
                ...doc.data()
            } as RemarkData;
            return {
                id: remarkData.id,
                text: remarkData.remark,
                time: remarkData.createdAt,
                type: 'remark', // Assuming type is always 'remark'
            };
        }) as RemarkWithUserData[];

        // check if there are more remarks
        const hasMore = remarksList.length === pageSize;
        const lastDocument = remarksSnapshot.docs[remarksSnapshot.docs.length - 1] || null;
        // const totalRemarkSnapshot = await getCountFromServer(remarksQuery);
        // const totalRemarks = totalRemarkSnapshot.data().count;
        return {
            data: {
                remarks: remarksList,
                lastDocument: {
                    id: lastDocument?.id,
                    lastRemarkDate: lastDocument?.data()?.createdAt
                },
                hasMore: hasMore,
                // count: totalRemarks
            }
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching remarks';
        console.error('Error in fetchRemarks:', error);
        throw new Error(errorMessage);
    }
}

// funtion for fetching emails of a lead
interface FetchEmailsRequest {
    leadId: string | undefined;
    lastDocumentId?: string;
    lastEmailDate?: Timestamp;
    pageSize?: number;
}

interface FetchEmailsResponse {
    data: {
        emails: EmailWithUserData[];
        lastDocument: {
            id: string;
            lastEmailDate: Timestamp;
        }
        hasMore: boolean;
        // count: number;
    }
}

interface EmailData {
    id: string;
    subject: string;
    to?: string[];
    cc?: string[];
    bcc?: string[];
    userId: string;
    leadId: string;
    createdAt: Timestamp;
}

export interface EmailWithUserData {
    id: string;
    text: string;
    time: Timestamp;
    type: string;
    userId: string;
    isDone: boolean;
    userName: string;
}

export async function fetchEmails(
    db: Firestore,
    user: UserData | null,
    request: FetchEmailsRequest
): Promise<FetchEmailsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch emails');
        }
        // Get user document
        const userData = user;
        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }
        // Set default page size or use requested size
        const pageSize = request.pageSize || 6;
        // Query emails with the same leadId
        if (!request.leadId) {
            throw new Error('leadId is required to fetch emails');
        }
        let emailsQuery = query(
            collection(db, 'companies', userData.companyId, 'emails'),
            where('leadId', '==', request.leadId),
            orderBy('createdAt', 'desc'),
            limit(pageSize)
        );
        // Apply pagination if lastDocumentId exists
        if (
            request.lastDocumentId !== undefined &&
            request.lastEmailDate !== undefined &&
            request.leadId !== undefined
        ) {
            try {
                const lastEmailDoc = await getDoc(
                    doc(
                        db,
                        'companies',
                        userData.companyId,
                        'emails',
                        request.lastDocumentId
                    )
                );
                if (lastEmailDoc.exists()) {
                    emailsQuery = query(
                        collection(db, 'companies', userData.companyId, 'emails'),
                        where('leadId', '==', request.leadId),
                        orderBy('createdAt', 'desc'),
                        startAfter(lastEmailDoc),
                        limit(pageSize)
                    );
                }
            }
            catch (error) {
                console.error('Error fetching last email:', error);
            }
        }
        const emailsSnapshot = await getDocs(emailsQuery);
        // Get all users
        const usersQuery = query(
            collection(db, 'users'),
            where('companyId', '==', userData.companyId)
        );
        const usersSnapshot = await getDocs(usersQuery);
        // Create a map of user IDs to user names for efficient lookup
        const userMap = new Map<string, string>();
        usersSnapshot.docs.forEach(doc => {
            const userData = doc.data() as UserData;
            userMap.set(doc.id, userData.name);
        });
        // Map emails and include user names
        const emailsList = emailsSnapshot.docs.map(doc => {
            const emailData = {
                id: doc.id,
                ...doc.data()
            } as EmailData;
            return {
                id: emailData.id,
                // text: `Subject: ${emailData.subject}, To: ${emailData?.to?.length ? emailData.to.join(', ') : 'none'}, CC: ${emailData?.cc?.length ? emailData.cc.join(', ') : 'none'}, BCC: ${emailData?.bcc?.length ? emailData.bcc.join(', ') : 'none'}`,
                text: `Subject: ${emailData.subject}`,
                time: emailData.createdAt,
                type: 'email',
                userId: emailData.userId,
                isDone: true,
                userName: userMap.get(emailData.userId) || 'Unknown User',
            };
        }) as EmailWithUserData[];
        // check if there are more emails
        const hasMore = emailsList.length === pageSize;
        const lastDocument = emailsSnapshot.docs[emailsSnapshot.docs.length - 1] || null;
        // const totalEmailSnapshot = await getCountFromServer(emailsQuery);
        // const totalEmails = totalEmailSnapshot.data().count;

        console.log("emailsList>>>>>>>>>>>>>>>>>>:", emailsList);

        return {
            data: {
                emails: emailsList,
                lastDocument: {
                    id: lastDocument.id,
                    lastEmailDate: lastDocument.data()?.createdAt
                },
                hasMore: hasMore,
                // count: totalEmails
            }
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching emails';
        console.error('Error in fetchEmails:', error);
        throw new Error(errorMessage);
    }
}



// Report Function

// common

interface LeadData {
    userId: string;
    id: string;
    name: string;
    phone: string;
    assignedTo: string;
    campaignId: string;
    stateId: string;
    admissionStatusId: string;
}

// function to get call logs for a report bsed on the given date range and find each lead from the call logs and group them uniquely based on the campaign they belong to

interface CallLogCampaignData {
    campaignName: string;
    connectedCalls: number;
    NotConnectedCalls: number;
    totalCalls: number;
    connectivityPercentage: string;
}



// Reports common interfaces

interface FetchReportRequest {
    startDate: Timestamp;
    endDate: Timestamp;
    isManager: boolean;
    isStaff: boolean;
}
//  Campaign Analytics


interface CallLogCampaignDataResponse {
    [x: string]: any;
    "Campaign": string;
    "Connected": number;
    "Not Connected": number;
    "Grand Total": number;
    "Connectivity": string;
}

interface FetchCampaignCallLogsForReportResponse {
    data: {
        callLogs: CallLogCampaignDataResponse[];
    }
}

export async function fetchCampaignCallLogsForReport(
    db: Firestore,
    user: User | null,
    request: FetchReportRequest
): Promise<FetchCampaignCallLogsForReportResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch call logs for report');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Query call logs with the same leadId
        let callLogsQuery = query(
            collection(db, 'companies', userData.companyId, 'callLogs'),
            where('callTimestamp', '>=', request.startDate),
            where('callTimestamp', '<=', request.endDate),
            orderBy('callTimestamp', 'desc')
        );

        if (request.isManager) {
            const usersQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData?.companyId),
                where('managerId', '==', user?.uid)
            );
            const usersSnapshot = await getDocs(usersQuery);
            const users = usersSnapshot.docs.map(doc => doc.id);

            callLogsQuery = query(
                collection(db, 'companies', userData.companyId, 'callLogs'),
                where('callTimestamp', '>=', request.startDate),
                where('callTimestamp', '<=', request.endDate),
                where('userId', 'in', users),
                orderBy('callTimestamp', 'desc')
            );
        } else if (request.isStaff) {
            callLogsQuery = query(
                collection(db, 'companies', userData.companyId, 'callLogs'),
                where('callTimestamp', '>=', request.startDate),
                where('callTimestamp', '<=', request.endDate),
                where('userId', '==', user?.uid),
                orderBy('callTimestamp', 'desc')
            );
        }

        const callLogsSnapshot = await getDocs(callLogsQuery);

        // get all call logs
        const callLogsList = callLogsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as CallLogData[];

        let leadsQuery = query(
            collection(db, 'companies', userData.companyId, 'leads'),
            where('createdAt', '>=', request.startDate),
            where('createdAt', '<=', request.endDate),
            where('isActive', '==', true),
        );

        if (request.isManager) {
            const usersQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData?.companyId),
                where('managerId', '==', user?.uid)
            );
            const usersSnapshot = await getDocs(usersQuery);
            const users = usersSnapshot.docs.map(doc => doc.id);

            leadsQuery = query(
                collection(db, 'companies', userData.companyId, 'leads'),
                where('createdAt', '>=', request.startDate),
                where('createdAt', '<=', request.endDate),
                where('isActive', '==', true),
                where('userId', 'in', users)
            );
        } else if (request.isStaff) {
            leadsQuery = query(
                collection(db, 'companies', userData.companyId, 'leads'),
                where('createdAt', '>=', request.startDate),
                where('createdAt', '<=', request.endDate),
                where('isActive', '==', true),
                where('userId', '==', user?.uid)
            );
        }

        const leadsSnapshot = await getDocs(leadsQuery);
        const leadsData = leadsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as LeadData[];

        // Helper function to ensure campaign stats are initialized
        const initializeCampaignStats = (campaignId: string) => {
            if (!leadConnectivityMap.has(campaignId)) {
                leadConnectivityMap.set(campaignId, {
                    connected: new Set<string>(),
                    notConnected: new Set<string>(),
                    notAttempted: new Set<string>(),
                    totalLeads: new Set<string>()
                });
            }
            return leadConnectivityMap.get(campaignId)!;
        };

        // Create a map to store campaign details and initialize connectivity map
        const campaignDetailsMap = new Map<string, string>();
        const leadConnectivityMap = new Map<string, {
            connected: Set<string>,
            notConnected: Set<string>,
            notAttempted: Set<string>,
            totalLeads: Set<string>
        }>();

        // Add "No Campaign" entry and initialize its stats
        campaignDetailsMap.set('no-campaign', 'No Campaign');
        initializeCampaignStats('no-campaign');

        // Fetch all campaigns for the company
        const campaignsSnapshot = await getDocs(
            collection(db, `companies/${userData.companyId}/leadCampaigns`)
        );

        // Populate campaign details map and initialize stats for each campaign
        campaignsSnapshot.docs.forEach(doc => {
            const campaignData = doc.data();
            campaignDetailsMap.set(doc.id, campaignData.campaign || 'Unnamed Campaign');
            initializeCampaignStats(doc.id);
        });

        // Create a map of lead IDs to campaign IDs
        const leadCampaignMap = new Map<string, string>();

        // Process all leads first
        leadsData.forEach(lead => {
            const campaignId = lead.campaignId || 'no-campaign';
            leadCampaignMap.set(lead.id, campaignId);

            // Initialize campaign stats if needed
            const campaignStats = initializeCampaignStats(campaignId);

            // Add lead to total leads and mark as not attempted
            campaignStats.totalLeads.add(lead.id);
            campaignStats.notAttempted.add(lead.id);
        });

        // Process call logs
        callLogsList.forEach(callLog => {
            const campaignId = leadCampaignMap.get(callLog.leadUid) || 'no-campaign';
            const campaignStats = initializeCampaignStats(campaignId);

            // Remove from not attempted since we found a call attempt
            campaignStats.notAttempted.delete(callLog.leadUid);

            // Update connected/not connected status
            if (callLog.duration > 0) {
                campaignStats.connected.add(callLog.leadUid);
                campaignStats.notConnected.delete(callLog.leadUid);
            } else if (!campaignStats.connected.has(callLog.leadUid)) {
                campaignStats.notConnected.add(callLog.leadUid);
            }
        });

        // Convert lead tracking to final report format
        const callLogsReport = Array.from(campaignDetailsMap.entries()).map(([campaignId, campaignName]) => {
            const stats = leadConnectivityMap.get(campaignId) || initializeCampaignStats(campaignId);

            const connectedLeads = stats.connected.size;
            const notConnectedLeads = stats.notConnected.size;
            const notAttemptedLeads = stats.notAttempted.size;
            const totalLeads = stats.totalLeads.size;

            return {
                "Campaign": campaignName,
                "Connected": connectedLeads,
                "Not Connected": notConnectedLeads,
                "Not Attempted": notAttemptedLeads,
                "Grand Total": totalLeads,
                "Connectivity": totalLeads > 0 ? `${((connectedLeads / totalLeads) * 100).toFixed(1)}%` : '0%'
            };
        });

        // Sort to ensure "No Campaign" appears at the end
        callLogsReport.sort((a, b) => {
            if (a.Campaign === 'No Campaign') return 1;
            if (b.Campaign === 'No Campaign') return -1;
            return a.Campaign.localeCompare(b.Campaign);
        });

        return {
            data: {
                callLogs: callLogsReport
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching call logs for report';
        console.error('Error in fetchCallLogsForReport:', error);
        throw new Error(errorMessage);
    }
}

interface FetchLeadsForReportResponse {
    data: {
        leads: any[];
    }
}

export async function fetchCampaignLeadsForReport(
    db: Firestore,
    user: User | null,
    request: FetchReportRequest
): Promise<FetchLeadsForReportResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch leads for report');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // get all leadStates from the company
        const leadStatesQuery = query(
            collection(db, 'companies', userData.companyId, 'leadStates')
        );

        const leadStatesSnapshot = await getDocs(leadStatesQuery);

        // Create an object with state as key and id as value
        const leadStates: { [key: string]: string } = {};
        const stateNames: string[] = [];
        leadStatesSnapshot.forEach((doc) => {
            const data = doc.data();
            leadStates[data.state] = doc.id;
            stateNames.push(data.state);
        });

        // Query leads with the same leadId
        let leadsQuery = query(
            collection(db, 'companies', userData.companyId, 'leads'),
            where('updatedAt', '>=', request.startDate),
            where('updatedAt', '<=', request.endDate),
            where('isActive', '==', true),
            orderBy('updatedAt', 'desc')
        );

        if (request.isManager) {
            const usersQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData?.companyId),
                where('managerId', '==', user?.uid)
            );
            const usersSnapshot = await getDocs(usersQuery);
            const users = usersSnapshot.docs.map(doc => doc.id);

            leadsQuery = query(
                collection(db, 'companies', userData.companyId, 'leads'),
                where('updatedAt', '>=', request.startDate),
                where('updatedAt', '<=', request.endDate),
                where('isActive', '==', true),
                where('assignedTo', 'in', users),
                orderBy('updatedAt', 'desc')
            );
        } else if (request.isStaff) {
            leadsQuery = query(
                collection(db, 'companies', userData.companyId, 'leads'),
                where('updatedAt', '>=', request.startDate),
                where('updatedAt', '<=', request.endDate),
                where('isActive', '==', true),
                where('assignedTo', '==', user?.uid),
                orderBy('updatedAt', 'desc')
            );
        }

        const leadsSnapshot = await getDocs(leadsQuery);
        const leadsList = leadsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as LeadData[];

        // Create a map to store campaign details
        const campaignDetailsMap = new Map<string, string>();
        const campaignsSnapshot = await getDocs(
            collection(db, `companies/${userData.companyId}/leadCampaigns`)
        );
        campaignsSnapshot.docs.forEach(doc => {
            const campaignData = doc.data();
            campaignDetailsMap.set(doc.id, campaignData.campaign || 'Unnamed Campaign');
        });

        // Create a map of lead IDs to campaign IDs
        const leadCampaignMap = new Map<string, string>();
        leadsList.forEach(lead => {
            leadCampaignMap.set(lead.id, lead.campaignId);
        });

        // Track lead state per campaign
        const leadStateMap = new Map<string, { [key: string]: number }>();

        // First pass: Track lead state
        leadsList.forEach(lead => {
            const campaignId = leadCampaignMap.get(lead.id) || 'no-campaign';

            if (!leadStateMap.has(campaignId)) {
                // Initialize counts for all possible states
                const initialStateCounts = Object.values(leadStates).reduce((acc, stateId) => {
                    acc[stateId] = 0;
                    return acc;
                }, {} as Record<string, number>);

                initialStateCounts['total'] = 0;
                leadStateMap.set(campaignId, initialStateCounts);
            }

            const campaignStates = leadStateMap.get(campaignId)!;
            campaignStates[lead.stateId] = (campaignStates[lead.stateId] || 0) + 1;
            campaignStates['total']++;
        });

        // Convert lead tracking to final report format
        const leadsReport = Array.from(leadStateMap.entries()).map(([campaignId, leadStats]) => {
            const campaignName = campaignDetailsMap.get(campaignId) || 'No Campaign';

            // Create the base report object with campaign name
            const report: Record<string, string | number> = {
                "Campaign": campaignName
            };

            // Add counts for each state
            stateNames.forEach(stateName => {
                const stateId = leadStates[stateName];
                report[stateName] = leadStats[stateId] || 0;
            });

            // Add total and potential ratio
            const totalLeads = leadStats['total'];
            const potentialLeads = leadStats[leadStates['Potential']] || 0;

            report["Grand Total"] = totalLeads;
            report["Potential Ratio"] = totalLeads > 0
                ? `${((potentialLeads / totalLeads) * 100).toFixed(1)}%`
                : '0%';

            return report;
        });

        return {
            data: {
                leads: leadsReport
            }
        };
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching leads for report';
        console.error('Error in fetchLeadsForReport:', error);
        throw new Error(errorMessage);
    }
}


// Employee Analytics

interface CallLogEmployeeDataResponse {
    "Employee": string;
    "Connected": number;
    "Not Connected": number;
    "Grand Total": number;
    "Connectivity": string;
}

interface FetchEmployeeCallLogsForReportResponse {
    data: {
        callLogs: CallLogEmployeeDataResponse[];
    }
}

export async function fetchEmployeeCallLogsForReport(
    db: Firestore,
    user: User | null,
    request: FetchReportRequest
): Promise<FetchEmployeeCallLogsForReportResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch call logs for report');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // First, get all relevant employees
        let employeeQuery = query(
            collection(db, 'users'),
            where('companyId', '==', userData.companyId)
        );

        if (request.isManager) {
            employeeQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData.companyId),
                where('managerId', '==', user?.uid)
            );
        } else if (request.isStaff) {
            employeeQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData.companyId),
                where(documentId(), '==', user?.uid)
            );
        }

        const employeeSnapshot = await getDocs(employeeQuery);

        // Create maps for employee data and initialize connectivity stats
        const employeeDetailsMap = new Map<string, string>();
        console.log('employeeDetailsMap 1 >>>', employeeDetailsMap);

        const leadConnectivityMap = new Map<string, {
            connected: Set<string>,
            notConnected: Set<string>,
            notAttempted: Set<string>,
            totalLeads: Set<string>
        }>();
        console.log('leadConnectivityMap 1 >>>', leadConnectivityMap);

        // Populate employee details and initialize stats
        employeeSnapshot.docs.forEach(doc => {
            const employeeData = doc.data();
            const employeeId = doc.id;
            employeeDetailsMap.set(employeeId, employeeData.name || 'Unnamed Employee');

            // Initialize stats for this employee
            leadConnectivityMap.set(employeeId, {
                connected: new Set<string>(),
                notConnected: new Set<string>(),
                notAttempted: new Set<string>(),
                totalLeads: new Set<string>()
            });
        });

        console.log('employeeDetailsMap 2 >>>', employeeDetailsMap);
        console.log('leadConnectivityMap 2 >>>', leadConnectivityMap);


        // Get all leads first
        let leadsQuery = query(
            collection(db, 'companies', userData.companyId, 'leads'),
            where('createdAt', '>=', request.startDate),
            where('createdAt', '<=', request.endDate),
            where('isActive', '==', true),
        );

        if (request.isManager) {
            const usersQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData?.companyId),
                where('managerId', '==', user?.uid)
            );
            const usersSnapshot = await getDocs(usersQuery);
            const users = usersSnapshot.docs.map(doc => doc.id);

            leadsQuery = query(
                collection(db, 'companies', userData.companyId, 'leads'),
                where('createdAt', '>=', request.startDate),
                where('createdAt', '<=', request.endDate),
                where('isActive', '==', true),
                where('userId', 'in', users)
            );
        } else if (request.isStaff) {
            leadsQuery = query(
                collection(db, 'companies', userData.companyId, 'leads'),
                where('createdAt', '>=', request.startDate),
                where('createdAt', '<=', request.endDate),
                where('isActive', '==', true),
                where('userId', '==', user?.uid)
            );
        }

        const leadsSnapshot = await getDocs(leadsQuery);

        // Process leads to populate totalLeads and notAttempted sets
        leadsSnapshot.docs.forEach(doc => {
            const lead = doc.data() as LeadData;
            const employeeId = lead.userId || 'no-employee';

            // Initialize stats for this employee if not already done
            if (!leadConnectivityMap.has(employeeId)) {
                leadConnectivityMap.set(employeeId, {
                    connected: new Set<string>(),
                    notConnected: new Set<string>(),
                    notAttempted: new Set<string>(),
                    totalLeads: new Set<string>()
                });
                employeeDetailsMap.set(employeeId, 'No Employee');
            }

            const stats = leadConnectivityMap.get(employeeId)!;
            stats.totalLeads.add(doc.id);
            stats.notAttempted.add(doc.id);
        });

        console.log('employeeDetailsMap 3 >>>', employeeDetailsMap);
        console.log('leadConnectivityMap 3 >>>', leadConnectivityMap);

        // Now get call logs
        let callLogsQuery = query(
            collection(db, 'companies', userData.companyId, 'callLogs'),
            where('callTimestamp', '>=', request.startDate),
            where('callTimestamp', '<=', request.endDate),
            orderBy('callTimestamp', 'desc')
        );

        if (request.isManager) {
            const usersQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData?.companyId),
                where('managerId', '==', user?.uid)
            );
            const usersSnapshot = await getDocs(usersQuery);
            const users = usersSnapshot.docs.map(doc => doc.id);

            callLogsQuery = query(
                collection(db, 'companies', userData.companyId, 'callLogs'),
                where('callTimestamp', '>=', request.startDate),
                where('callTimestamp', '<=', request.endDate),
                where('userId', 'in', users),
                orderBy('callTimestamp', 'desc')
            );
        } else if (request.isStaff) {
            callLogsQuery = query(
                collection(db, 'companies', userData.companyId, 'callLogs'),
                where('callTimestamp', '>=', request.startDate),
                where('callTimestamp', '<=', request.endDate),
                where('userId', '==', user?.uid),
                orderBy('callTimestamp', 'desc')
            );
        }

        const callLogsSnapshot = await getDocs(callLogsQuery);

        // Process call logs to update connectivity status
        callLogsSnapshot.docs.forEach(doc => {
            const callLog = doc.data() as CallLogData;
            const employeeId = callLog.userId || 'no-employee';

            // Initialize stats for this employee if not already done
            if (!leadConnectivityMap.has(employeeId)) {
                leadConnectivityMap.set(employeeId, {
                    connected: new Set<string>(),
                    notConnected: new Set<string>(),
                    notAttempted: new Set<string>(),
                    totalLeads: new Set<string>()
                });
                employeeDetailsMap.set(employeeId, 'No Employee');
            }

            const stats = leadConnectivityMap.get(employeeId)!;

            stats.totalLeads.add(callLog.leadUid);

            // Remove from notAttempted since we found a call attempt
            stats.notAttempted.delete(callLog.leadUid);

            // Update connected/not connected status
            if (callLog.duration > 0) {
                stats.connected.add(callLog.leadUid);
                stats.notConnected.delete(callLog.leadUid);
            } else if (!stats.connected.has(callLog.leadUid)) {
                stats.notConnected.add(callLog.leadUid);
            }
        });

        console.log('employeeDetailsMap 4 >>>', employeeDetailsMap);
        console.log('leadConnectivityMap 4 >>>', leadConnectivityMap);

        // Convert to final report format
        const callLogsReport = Array.from(employeeDetailsMap.entries()).map(([employeeId, employeeName]) => {
            const stats = leadConnectivityMap.get(employeeId) || {
                connected: new Set<string>(),
                notConnected: new Set<string>(),
                notAttempted: new Set<string>(),
                totalLeads: new Set<string>()
            };

            const connectedLeads = stats.connected.size;
            const notConnectedLeads = stats.notConnected.size;
            const notAttemptedLeads = stats.notAttempted.size;
            const totalLeads = stats.totalLeads.size;

            return {
                "Id": employeeId,
                "Employee": employeeName,
                "Connected": connectedLeads,
                "Not Connected": notConnectedLeads,
                "Not Attempted": notAttemptedLeads,
                "Grand Total": totalLeads,
                "Connectivity": totalLeads > 0 ? `${((connectedLeads / totalLeads) * 100).toFixed(1)}%` : '0%'
            };
        });

        console.log('callLogsReport >>>', callLogsReport);

        // Sort to ensure "No Employee" appears at the end
        callLogsReport.sort((a, b) => {
            if (a.Employee === 'No Employee') return 1;
            if (b.Employee === 'No Employee') return -1;
            return a.Employee.localeCompare(b.Employee);
        });

        return {
            data: {
                callLogs: callLogsReport
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching call logs for report';
        console.error('Error in fetchCallLogsForReport:', error);
        throw new Error(errorMessage);
    }
}

// Updated interface to match the actual data being returned
// interface CallLogEmployeeDataResponse {
//     "Employee": string;
//     "Connected": number;
//     "Not Connected": number;
//     "Not Attempted": number;  // Added missing field
//     "Grand Total": number;
//     "Connectivity": string;
// }

// interface FetchEmployeeCallLogsForReportResponse {
//     data: {
//         callLogs: CallLogEmployeeDataResponse[];
//     }
// }

// export async function fetchEmployeeCallLogsForReport(
//     db: Firestore,
//     user: User | null,
//     request: FetchReportRequest
// ): Promise<FetchEmployeeCallLogsForReportResponse> {
//     try {
//         // Check authentication
//         if (!user) {
//             throw new Error('Only authenticated users can fetch call logs for report');
//         }

//         // Get user document
//         const userDoc = await getDoc(doc(db, 'users', user.uid));
//         const userData = userDoc.data() as UserData;

//         if (!userData || !userData.companyId) {
//             throw new Error('No company associated with your account');
//         }

//         // Build employee query based on user role
//         let employeeQuery = query(
//             collection(db, 'users'),
//             where('companyId', '==', userData.companyId)
//         );

//         if (request.isManager) {
//             employeeQuery = query(
//                 collection(db, 'users'),
//                 where('companyId', '==', userData.companyId),
//                 where('managerId', '==', user.uid)
//             );
//         } else if (request.isStaff) {
//             employeeQuery = query(
//                 collection(db, 'users'),
//                 where('companyId', '==', userData.companyId),
//                 where('id', '==', user.uid)
//             );
//         }

//         const employeeSnapshot = await getDocs(employeeQuery);
//         const employeeIds = employeeSnapshot.docs.map(doc => doc.id);

//         // Create employee details map
//         const employeeDetailsMap = new Map<string, string>();
//         employeeSnapshot.docs.forEach(doc => {
//             const employeeData = doc.data();
//             employeeDetailsMap.set(doc.id, employeeData.name || 'Unnamed Employee');
//         });

//         // If no employees found, return empty result
//         if (employeeIds.length === 0) {
//             return {
//                 data: {
//                     callLogs: []
//                 }
//             };
//         }

//         // Get leads for the specified employees and date range
//         let leadsQuery = query(
//             collection(db, 'companies', userData.companyId, 'leads'),
//             where('createdAt', '>=', request.startDate),
//             where('createdAt', '<=', request.endDate),
//             where('isActive', '==', true),
//             where('userId', 'in', employeeIds)
//         );

//         const leadsSnapshot = await getDocs(leadsQuery);

//         // Get call logs for the specified employees and date range
//         let callLogsQuery = query(
//             collection(db, 'companies', userData.companyId, 'callLogs'),
//             where('callTimestamp', '>=', request.startDate),
//             where('callTimestamp', '<=', request.endDate),
//             where('userId', 'in', employeeIds),
//             orderBy('callTimestamp', 'desc')
//         );

//         const callLogsSnapshot = await getDocs(callLogsQuery);

//         // Process data more efficiently
//         const employeeStats = new Map<string, {
//             totalLeads: Set<string>,
//             connectedLeads: Set<string>,
//             notConnectedLeads: Set<string>,
//             attemptedLeads: Set<string>
//         }>();

//         // Initialize stats for all employees
//         employeeIds.forEach(employeeId => {
//             employeeStats.set(employeeId, {
//                 totalLeads: new Set<string>(),
//                 connectedLeads: new Set<string>(),
//                 notConnectedLeads: new Set<string>(),
//                 attemptedLeads: new Set<string>()
//             });
//         });

//         // Process leads to get total counts per employee
//         leadsSnapshot.docs.forEach(doc => {
//             const lead = doc.data() as LeadData;
//             const employeeId = lead.userId;

//             if (employeeId && employeeStats.has(employeeId)) {
//                 const stats = employeeStats.get(employeeId)!;
//                 stats.totalLeads.add(doc.id);
//             }
//         });

//         // Process call logs to determine connectivity status
//         const leadCallStatus = new Map<string, { connected: boolean, attempted: boolean }>();

//         callLogsSnapshot.docs.forEach(doc => {
//             const callLog = doc.data() as CallLogData;
//             const leadId = callLog.leadUid;
//             const employeeId = callLog.userId;

//             if (!leadId || !employeeId || !employeeStats.has(employeeId)) {
//                 return;
//             }

//             // Track that this lead was attempted
//             const stats = employeeStats.get(employeeId)!;
//             stats.attemptedLeads.add(leadId);

//             // Update the lead's call status
//             const currentStatus = leadCallStatus.get(leadId) || { connected: false, attempted: false };
//             currentStatus.attempted = true;

//             // If this call was connected (duration > 0), mark as connected
//             if (callLog.duration > 0) {
//                 currentStatus.connected = true;
//             }

//             leadCallStatus.set(leadId, currentStatus);
//         });

//         // Now categorize leads for each employee
//         employeeIds.forEach(employeeId => {
//             const stats = employeeStats.get(employeeId)!;

//             stats.totalLeads.forEach(leadId => {
//                 const callStatus = leadCallStatus.get(leadId);

//                 if (!callStatus || !callStatus.attempted) {
//                     // Lead was not attempted
//                     return;
//                 } else if (callStatus.connected) {
//                     // Lead was connected
//                     stats.connectedLeads.add(leadId);
//                 } else {
//                     // Lead was attempted but not connected
//                     stats.notConnectedLeads.add(leadId);
//                 }
//             });
//         });

//         // Convert to final report format
//         const callLogsReport: CallLogEmployeeDataResponse[] = Array.from(employeeDetailsMap.entries()).map(([employeeId, employeeName]) => {
//             const stats = employeeStats.get(employeeId)!;

//             const totalLeads = stats.totalLeads.size;
//             const connectedLeads = stats.connectedLeads.size;
//             const notConnectedLeads = stats.notConnectedLeads.size;
//             const attemptedLeads = stats.attemptedLeads.size;
//             const notAttemptedLeads = totalLeads - attemptedLeads;

//             return {
//                 "Employee": employeeName,
//                 "Connected": connectedLeads,
//                 "Not Connected": notConnectedLeads,
//                 "Not Attempted": notAttemptedLeads,
//                 "Grand Total": totalLeads,
//                 "Connectivity": totalLeads > 0 ? `${((connectedLeads / totalLeads) * 100).toFixed(1)}%` : '0%'
//             };
//         });

//         // Sort alphabetically by employee name
//         callLogsReport.sort((a, b) => a.Employee.localeCompare(b.Employee));

//         return {
//             data: {
//                 callLogs: callLogsReport
//             }
//         };

//     } catch (error) {
//         const errorMessage = error instanceof Error ? error.message : 'Error fetching call logs for report';
//         console.error('Error in fetchEmployeeCallLogsForReport:', error);
//         throw new Error(errorMessage);
//     }
// }


interface FetchLeadsForReportResponse {
    data: {
        leads: any[];
    }
}

export async function fetchEmployeesLeadsForReport(
    db: Firestore,
    user: User | null,
    request: FetchReportRequest
): Promise<FetchLeadsForReportResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch leads for report');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // get all leadStates from the company
        const leadStatesQuery = query(
            collection(db, 'companies', userData.companyId, 'leadStates')
        );

        const leadStatesSnapshot = await getDocs(leadStatesQuery);

        // Create an object with state as key and id as value
        const leadStates: { [key: string]: string } = {};
        const stateNames: string[] = [];
        leadStatesSnapshot.forEach((doc) => {
            const data = doc.data();
            leadStates[data.state] = doc.id;
            stateNames.push(data.state);
        });

        // Query leads with the same leadId
        let leadsQuery = query(
            collection(db, 'companies', userData.companyId, 'leads'),
            where('updatedAt', '>=', request.startDate),
            where('updatedAt', '<=', request.endDate),
            where('isActive', '==', true),
            orderBy('updatedAt', 'desc')
        );

        if (request.isManager) {
            const usersQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData?.companyId),
                where('managerId', '==', user?.uid)
            );
            const usersSnapshot = await getDocs(usersQuery);
            const users = usersSnapshot.docs.map(doc => doc.id);

            leadsQuery = query(
                collection(db, 'companies', userData.companyId, 'leads'),
                where('updatedAt', '>=', request.startDate),
                where('updatedAt', '<=', request.endDate),
                where('isActive', '==', true),
                where('assignedTo', 'in', users),
                orderBy('updatedAt', 'desc')
            );
        } else if (request.isStaff) {
            leadsQuery = query(
                collection(db, 'companies', userData.companyId, 'leads'),
                where('updatedAt', '>=', request.startDate),
                where('updatedAt', '<=', request.endDate),
                where('isActive', '==', true),
                where('assignedTo', '==', user?.uid),
                orderBy('updatedAt', 'desc')
            );
        }

        const leadsSnapshot = await getDocs(leadsQuery);
        const leadsList = leadsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as LeadData[];

        // Create a map to store employee details
        const employeeDetailsMap = new Map<string, string>();
        let employeeQuery = query(
            collection(db, 'users'),
            where('companyId', '==', userData.companyId)
        );

        if (request.isManager) {
            employeeQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData.companyId),
                where('managerId', '==', user?.uid)
            );
        } else if (request.isStaff) {
            employeeQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData.companyId),
                where('id', '==', user?.uid)
            );
        }

        const employeeSnapshot = await getDocs(employeeQuery);
        employeeSnapshot.docs.forEach(doc => {
            const employeeData = doc.data();
            employeeDetailsMap.set(doc.id, employeeData.name || 'Unnamed Employee');
        });

        // Create a map of lead IDs to employee IDs
        const leadEmployeeMap = new Map<string, string>();
        leadsList.forEach(lead => {
            leadEmployeeMap.set(lead.id, lead.assignedTo);
        });

        // Track lead state per employee
        const leadStateMap = new Map<string, { [key: string]: number }>();

        // First pass: Track lead state
        leadsList.forEach(lead => {
            const employeeId = leadEmployeeMap.get(lead.id) || 'no-employee';

            if (!leadStateMap.has(employeeId)) {
                // Initialize counts for all possible states
                const initialStateCounts = Object.values(leadStates).reduce((acc, stateId) => {
                    acc[stateId] = 0;
                    return acc;
                }, {} as Record<string, number>);

                initialStateCounts['total'] = 0;
                leadStateMap.set(employeeId, initialStateCounts);
            }

            const employeeStates = leadStateMap.get(employeeId)!;
            employeeStates[lead.stateId] = (employeeStates[lead.stateId] || 0) + 1;
            employeeStates['total']++;
        });

        // Convert lead tracking to final report format
        const leadsReport = Array.from(leadStateMap.entries()).map(([employeeId, leadStats]) => {
            const employeeName = employeeDetailsMap.get(employeeId) || 'No Employee';

            // Create the base report object with employee name
            const report: Record<string, string | number> = {
                "Employee": employeeName
            };

            // Add counts for each state
            stateNames.forEach(stateName => {
                const stateId = leadStates[stateName];
                report[stateName] = leadStats[stateId] || 0;
            });

            // Add total and potential ratio
            const totalLeads = leadStats['total'];
            const potentialLeads = leadStats[leadStates['Potential']] || 0;

            report["Grand Total"] = totalLeads;
            report["Potential Ratio"] = totalLeads > 0
                ? `${((potentialLeads / totalLeads) * 100).toFixed(1)}%`
                : '0%';

            return report;
        });

        return {
            data: {
                leads: leadsReport
            }
        };
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching leads for report';
        console.error('Error in fetchLeadsForReport:', error);
        throw new Error(errorMessage);
    }
}

// Admission Analytics

// function to map the admission status to the employees based on the leads assigned to them, the headers are employees and the rows are the admission status

interface FetchEmployeeAdmissionStatusForReportResponse {
    data: {
        admissionStatus: any[];
    }
}

export async function fetchEmployeeAdmissionStatusForReport(
    db: Firestore,
    user: User | null,
    request: FetchReportRequest
): Promise<FetchEmployeeAdmissionStatusForReportResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch admission status for report');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Query leads with the same leadId
        let leadsQuery = query(
            collection(db, 'companies', userData.companyId, 'leads'),
            where('updatedAt', '>=', request.startDate),
            where('updatedAt', '<=', request.endDate),
            where('isActive', '==', true),
            orderBy('updatedAt', 'desc')
        );

        if (request.isManager) {
            const usersQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData?.companyId),
                where('managerId', '==', user?.uid)
            );
            const usersSnapshot = await getDocs(usersQuery);
            const users = usersSnapshot.docs.map(doc => doc.id);

            leadsQuery = query(
                collection(db, 'companies', userData.companyId, 'leads'),
                where('updatedAt', '>=', request.startDate),
                where('updatedAt', '<=', request.endDate),
                where('isActive', '==', true),
                where('assignedTo', 'in', users),
                orderBy('updatedAt', 'desc')
            );
        } else if (request.isStaff) {
            leadsQuery = query(
                collection(db, 'companies', userData.companyId, 'leads'),
                where('updatedAt', '>=', request.startDate),
                where('updatedAt', '<=', request.endDate),
                where('assignedTo', '==', user?.uid),
                where('isActive', '==', true),
                orderBy('updatedAt', 'desc')
            );
        }

        const leadsSnapshot = await getDocs(leadsQuery);
        const leadsList = leadsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as LeadData[];

        // get all admissionStatus from the company
        const admissionStatusQuery = query(
            collection(db, 'companies', userData.companyId, 'leadAdmissionStatus')
        );

        const admissionStatusSnapshot = await getDocs(admissionStatusQuery);

        // Create an object with status as key and id as value
        const admissionStatus: { [key: string]: string } = {};
        const statusNames: string[] = [];
        const statusIDs: string[] = [];
        admissionStatusSnapshot.forEach((doc) => {
            const data = doc.data();
            admissionStatus[data.admissionStatus] = doc.id;
            statusNames.push(data.admissionStatus);

            // Add status ID to list
            statusIDs.push(doc.id);
        });

        // Create a map to store employee details
        const employeeDetailsMap = new Map<string, string>();
        let employeeQuery = query(
            collection(db, 'users'),
            where('companyId', '==', userData.companyId)
        );

        if (request.isManager) {
            employeeQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData.companyId),
                where('managerId', '==', user?.uid)
            );
        } else if (request.isStaff) {
            employeeQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData.companyId),
                where('id', '==', user?.uid)
            );
        }

        const employeeSnapshot = await getDocs(employeeQuery);
        employeeSnapshot.docs.forEach(doc => {
            const employeeData = doc.data();
            employeeDetailsMap.set(doc.id, employeeData.name || 'Unnamed Employee');
        });

        // Create a map of lead IDs to employee IDs
        const leadEmployeeMap = new Map<string, string>();
        leadsList.forEach(lead => {
            leadEmployeeMap.set(lead.id, lead.assignedTo);
        });

        // Track lead state per employee
        const leadStateMap = new Map<string, { [key: string]: number }>();

        // First pass: Track lead state
        leadsList.forEach(lead => {
            const employeeId = leadEmployeeMap.get(lead.id) || 'no-employee';

            if (!leadStateMap.has(employeeId)) {
                // Initialize counts for all possible states
                const initialStateCounts = Object.values(admissionStatus).reduce((acc, admissionStatusId) => {
                    acc[admissionStatusId] = 0;
                    return acc;
                }, {} as Record<string, number>);

                initialStateCounts['total'] = 0;
                leadStateMap.set(employeeId, initialStateCounts);
            }

            const employeeStates = leadStateMap.get(employeeId)!;
            employeeStates[lead.admissionStatusId] = (employeeStates[lead.admissionStatusId] || 0) + 1;
            employeeStates['total']++;
        });

        // Convert lead tracking to final report format
        const admissionStatusReport = Array.from(leadStateMap.entries()).map(([employeeId, leadStats]) => {
            const employeeName = employeeDetailsMap.get(employeeId) || 'No Employee';

            // Create the base report object with employee name
            const report: Record<string, string | number> = {
                "Employee": employeeName,
                "Id": employeeId,
            };

            // Add counts for each state
            statusNames.forEach(statusName => {
                const admissionStatusId = admissionStatus[statusName];
                report[statusName] = leadStats[admissionStatusId] || 0;
            });

            // Add total and potential ratio
            const totalLeads = leadStats['total'];

            report["No Admission"] = totalLeads - Object.values(admissionStatus).reduce((acc, statusId) => {
                return acc + (leadStats[statusId] || 0);
            }, 0);

            report["Grand Total"] = totalLeads;

            return report;
        }
        );

        return {
            data: {
                admissionStatus: admissionStatusReport
            }
        };
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching admission status for report';
        console.error('Error in fetchAdmissionStatusForReport:', error);
        throw new Error(errorMessage);
    }
}

// function to get the data of leads based on the admission status and the employee as assignedTo.

interface FetchAdmissionStatusLeadsForReportResponse {
    data: {
        leads: any[];
    }
}

export async function fetchLeadsBasedOnAdmissionStatus(
    db: Firestore,
    user: User | null,
    request: {
        startDate: Timestamp;
        endDate: Timestamp;
        admissionStatusName: string;
        assignedTo: string;
    }
): Promise<FetchAdmissionStatusLeadsForReportResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch leads for report');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request.admissionStatusName) {
            throw new Error('No admission status provided');
        }

        if (request.admissionStatusName === 'No Admission' || request.admissionStatusName === 'Grand Total') {
            return {
                data: {
                    leads: []
                }
            };
        }

        const admissionStatusQuery = query(
            collection(db, 'companies', userData.companyId, 'leadAdmissionStatus'),
            where('admissionStatus', '==', request.admissionStatusName)
        );

        const admissionStatusSnapshot = await getDocs(admissionStatusQuery);

        if (admissionStatusSnapshot.empty) {
            throw new Error('Admission status not found');
        }

        const admissionStatusId = admissionStatusSnapshot.docs[0].id;

        // get all leadStates from the company
        const leadStatesQuery = query(
            collection(db, 'companies', userData.companyId, 'leads'),
            where('updatedAt', '>=', request.startDate),
            where('updatedAt', '<=', request.endDate),
            where('assignedTo', '==', request.assignedTo),
            where('admissionStatusId', '==', admissionStatusId),
            where('isActive', '==', true),
            orderBy('updatedAt', 'desc')
        );

        const leadStatesSnapshot = await getDocs(leadStatesQuery);

        // return the leads data
        const leadsData = leadStatesSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name,
            phone: doc.data().number,
        })) as LeadData[];

        return {
            data: {
                leads: leadsData
            }
        };

    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching leads for report';
        console.error('Error in fetchLeadsForReport:', error);
        throw new Error(errorMessage);
    }
}

// Function for fetching transfer logs of a lead
interface FetchTransferLogsRequest {
    leadId: string | undefined;
    lastDocumentId?: string;
    lastTransferDate?: Timestamp;
    pageSize?: number;
}

interface FetchTransferLogsResponse {
    data: {
        transferLogs: TransferLogWithUserData[];
        lastDocument: {
            id: string;
            lastTransferDate: Timestamp;
        }
        hasMore: boolean;
    }
}

interface TransferLogData {
    id: string;
    companyId: string;
    leadId: string;
    exAssignedTo: string;
    exAssignedName: string;
    newAssignedTo: string;
    newAssignedName: string;
    createdAt: Timestamp;
    updatedAt: Timestamp;
    userId: string;
}

export interface TransferLogWithUserData {
    id: string;
    text: string;
    time: Timestamp;
    type: string;
    userId: string;
    userName: string;
    exAssignedTo: string;
    exAssignedName: string;
    newAssignedTo: string;
    newAssignedName: string;
}

export async function fetchTransferLogs(
    db: Firestore,
    user: UserData | null,
    request: FetchTransferLogsRequest
): Promise<FetchTransferLogsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch transfer logs');
        }

        // Get user document
        const userData = user;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Set default page size or use requested size
        const pageSize = request.pageSize || 6;

        // Query transfer logs with the same leadId
        if (!request.leadId) {
            throw new Error('leadId is required to fetch transfer logs');
        }

        let transferLogsQuery = query(
            collection(db, 'companies', userData.companyId, 'transferLogs'),
            where('leadId', '==', request.leadId),
            orderBy('createdAt', 'desc'),
            limit(pageSize)
        );

        // Apply pagination if lastDocumentId exists
        if (request.lastDocumentId && request.lastTransferDate) {
            try {
                const lastTransferLogDoc = await getDoc(
                    doc(db, 'companies', userData.companyId, 'transferLogs', request.lastDocumentId)
                );
                if (lastTransferLogDoc.exists()) {
                    transferLogsQuery = query(
                        collection(db, 'companies', userData.companyId, 'transferLogs'),
                        where('leadId', '==', request.leadId),
                        orderBy('createdAt', 'desc'),
                        startAfter(lastTransferLogDoc),
                        limit(pageSize)
                    );
                }
            } catch (error) {
                console.error('Error fetching last transfer log:', error);
            }
        }

        const transferLogsSnapshot = await getDocs(transferLogsQuery);

        // Get all users
        const usersQuery = query(
            collection(db, 'users'),
            where('companyId', '==', userData.companyId)
        );

        const usersSnapshot = await getDocs(usersQuery);
        // Create a map of user IDs to user names for efficient lookup
        const userMap = new Map<string, string>();
        usersSnapshot.docs.forEach(doc => {
            const userData = doc.data() as UserData;
            userMap.set(doc.id, userData.name);
        });

        // Map transfer logs and include user names
        const transferLogsList = transferLogsSnapshot.docs.map(doc => {
            const transferLogData = {
                id: doc.id,
                ...doc.data()
            } as TransferLogData;

            return {
                id: transferLogData.id,
                text: `Lead transferred from ${transferLogData.exAssignedName} to ${transferLogData.newAssignedName}`,
                time: transferLogData.createdAt,
                type: 'transferLog',
                userId: transferLogData.userId,
                userName: userMap.get(transferLogData.userId) || 'Unknown User',
                exAssignedTo: transferLogData.exAssignedTo,
                exAssignedName: transferLogData.exAssignedName,
                newAssignedTo: transferLogData.newAssignedTo,
                newAssignedName: transferLogData.newAssignedName,
            };
        }) as TransferLogWithUserData[];

        // Check if there are more transfer logs
        const hasMore = transferLogsList.length === pageSize;
        const lastDocument = transferLogsSnapshot.docs[transferLogsSnapshot.docs.length - 1] || null;

        return {
            data: {
                transferLogs: transferLogsList,
                lastDocument: {
                    id: lastDocument?.id,
                    lastTransferDate: lastDocument?.data()?.createdAt
                },
                hasMore: hasMore,
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching transfer logs';
        console.error('Error in fetchTransferLogs:', error);
        throw new Error(errorMessage);
    }
}